import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  SeatMapOverview,
  SeatMapSlotResource,
  ResourceCategory,
  Scheduling,
  ScheduleTime,
} from '@src/core/buyerApiClient';
import { useAppSelector, useAppDispatch } from '../../core/hooks';
import { loadSeatMap, productSelector, schedulingSeatMapSelector, SelectedResource } from '@src/features/product';
import useTranslate from '../../i18n/useTranslate';
import SeatMapCanvas from './SeatMapCanvas';
import SeatMapSelection from './SeatMapSelection';
import { classNamespaceFactory } from '@src/common';
import { SelectedTariff, getTariffPriceForScheduleTime } from '@src/common/tariffs';
import {
  isValidStatus,
  AllocatedResource,
  getResourceAnalyticsData,
  makeResourceTariffValues,
  getResourceTariffValuesQuantity,
  getTotalCapacity,
  treeshakeUnnecessaryResources,
  seatMapResourceList,
  getMovableRemainingQuantity,
  getMaxAllowedCapacityForResourceList,
  getResourceStatus,
  getAllocatedResources,
} from '@src/common/seatMaps';
import { device } from '@src/common/breakpoints';
import { track } from '@src/services/analytics';
import useSelectedProductId from '@src/hooks/useSelectedProductId';

interface SeatMapSelectorProps {
  scheduling: Scheduling;
  timeMinutes: ScheduleTime | null;
  onSelectionChange: (resources: SelectedResource[], isReady: boolean, exceedingValueCents: number) => void;
  selectedTariffs: SelectedTariff[];
}

const SeatMapSelector = ({ scheduling, timeMinutes, onSelectionChange, selectedTariffs }: SeatMapSelectorProps) => {
  const { i18nElement } = useTranslate();
  const dispatch = useAppDispatch();
  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling.id, timeMinutes?.minutes));

  useEffect(() => {
    dispatch(loadSeatMap(scheduling, timeMinutes?.minutes));
  }, [dispatch, scheduling.id, timeMinutes?.minutes]);

  if (seatMap == null) {
    return (
      <div>
        <div>{i18nElement('base_loading')}</div>
      </div>
    );
  }

  return <SeatMapSelectorFinal {...{ onSelectionChange, selectedTariffs, timeMinutes }} seatMap={seatMap!} />;
};

interface SeatMapSelectorFinalProps {
  seatMap: SeatMapOverview;
  onSelectionChange: (resources: SelectedResource[], isReady: boolean, exceedingValueCents: number) => void;
  selectedTariffs: SelectedTariff[];
  timeMinutes: ScheduleTime | null;
}

const SeatMapSelectorFinal = ({
  seatMap,
  selectedTariffs,
  onSelectionChange,
  timeMinutes,
}: SeatMapSelectorFinalProps) => {
  const prodId = useSelectedProductId();
  const product = useAppSelector(productSelector(prodId));

  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [tariffValues, setTariffValues] = useState<{ [key: string]: number }>({});

  const resourceList = useMemo<SeatMapSlotResource[]>(() => seatMapResourceList(seatMap), [seatMap.areas]);
  const resourceIndex = useMemo<{ [key: string]: SeatMapSlotResource }>(
    () => resourceList.reduce((carry, res) => ({ ...carry, [res.id]: res }), {}),
    [resourceList],
  );
  const categoryIndex = useMemo<{ [key: string]: ResourceCategory }>(
    () => seatMap.categories.reduce((carry, cat) => ({ ...carry, [cat.id]: cat }), {}),
    [seatMap.categories],
  );

  const getResources = (ids: string[]) => ids.map((id) => resourceIndex[id]!);

  const tariffSum = selectedTariffs.reduce((sum, { quantity }) => sum + quantity, 0);
  const selectedResources = getResources(selectedResourceIds);
  const selectedCapacitySum = getTotalCapacity(selectedResources);
  const remainingQuantityToAllocate = tariffSum - selectedCapacitySum;
  const maxCapacity = useMemo<number | null>(
    () => getMaxAllowedCapacityForResourceList(resourceList, remainingQuantityToAllocate),
    [resourceList, remainingQuantityToAllocate],
  );
  const pendingAmount = Math.max(remainingQuantityToAllocate, 0);
  const movableRemainingQuantity = useMemo<number>(
    () => getMovableRemainingQuantity(selectedResources, selectedTariffs, categoryIndex, tariffValues),
    [selectedResources, selectedTariffs, categoryIndex, tariffValues],
  );

  const allocatedResources = useMemo<AllocatedResource[]>(
    () =>
      getAllocatedResources(
        selectedResources,
        selectedTariffs,
        categoryIndex,
        tariffValues,
        remainingQuantityToAllocate,
      ),
    [selectedResources, selectedTariffs, categoryIndex, tariffValues, remainingQuantityToAllocate],
  );

  const allAllocated = pendingAmount === 0;
  const isReady = allAllocated && allocatedResources.reduce((ok, { isReady }) => ok && isReady, true);

  const totalTariffValueCents = selectedTariffs.reduce((sum, st) => {
    const tariffPriceCents = getTariffPriceForScheduleTime(st.tariffsPerType, timeMinutes)?.priceCents ?? 0;
    return sum + tariffPriceCents * st.quantity;
  }, 0);
  const sumAllocatedMinValue = allocatedResources.reduce((sum, ar) => sum + ar.resourceValueCents, 0);
  const exceedingValueCents = Math.max(sumAllocatedMinValue - totalTariffValueCents, 0);

  // Send selected resources and status
  useEffect(() => {
    const values: SelectedResource[] = selectedResources.map((resourceSlot) => ({
      resourceSlot,
      tariffs: selectedTariffs
        .map(({ tariff }) => ({
          tariffId: tariff.id,
          quantity: getResourceTariffValuesQuantity(resourceSlot, tariff, tariffValues),
        }))
        .filter(({ quantity }) => quantity > 0),
    }));

    onSelectionChange(values, isReady, exceedingValueCents);
  }, [selectedResourceIds]);

  const resourceStatus = (resource: SeatMapSlotResource) => {
    return getResourceStatus(
      resource,
      selectedResourceIds,
      tariffSum,
      categoryIndex,
      maxCapacity,
      selectedCapacitySum,
      remainingQuantityToAllocate,
      movableRemainingQuantity,
    );
  };

  const handleSelectionChange = (res: SeatMapSlotResource) => {
    if (!isValidStatus(resourceStatus(res))) {
      return;
    }

    let newIds = selectedResourceIds.includes(res.id)
      ? selectedResourceIds.filter((id) => id !== res.id)
      : [...selectedResourceIds, res.id];

    newIds = treeshakeUnnecessary(newIds);

    const newTariffValues = mountTariffValues(newIds);

    setSelectedResourceIds(newIds);
    setTariffValues(newTariffValues);
  };

  const handleSelectResource = (resource: SeatMapSlotResource) => {
    track('productAdderResourceClick', getResourceAnalyticsData(product!, resource));

    handleSelectionChange(resource);
  };

  const handleRemoveResource = (resource: SeatMapSlotResource) => {
    track('productAdderResourceRemoveClick', getResourceAnalyticsData(product!, resource));

    handleSelectionChange(resource);
  };

  const treeshakeUnnecessary = (ids: string[]) =>
    treeshakeUnnecessaryResources(getResources(ids), tariffSum).map((res) => res.id);
  const mountTariffValues = (ids: string[]) =>
    makeResourceTariffValues(selectedTariffs, getResources(ids), categoryIndex);

  const cn = classNamespaceFactory('seat-map-selector');

  return (
    <SeatMapSelectorRoot>
      <CanvasContainer>
        <SeatMapCanvas {...{ cn, resourceStatus, seatMap }} onSelection={handleSelectResource} />
      </CanvasContainer>
      <SidebarContainer>
        <SeatMapSelection
          {...{
            seatMap,
            allocatedResources,
            tariffSum,
            selectedCapacitySum,
            allAllocated,
            isReady,
            onRemove: handleRemoveResource,
          }}
        />
      </SidebarContainer>
    </SeatMapSelectorRoot>
  );
};

const SeatMapSelectorRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${device.smallTablet} {
    flex-direction: row;
  }
`;

const CanvasContainer = styled.div`
  flex: auto;

  ${device.large} {
    min-width: 450px;
  }
`;

const SidebarContainer = styled.div`
  flex: 1;

  ${device.large} {
    min-width: 510px;
  }

  ${device.smallTablet} {
    flex: 0 1 360px;
  }
`;

export default SeatMapSelector;
